import { Component, DestroyRef, OnDestroy, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Lux } from '@roosevelt/common-ui-lib/core';
import { IClient, IClients } from 'src/app/shared/types/shared-state';
import { getSelectedAdminPlan } from 'src/app/login/store/selectors';
import { ILoginState } from 'src/app/login/store/state';
import { CreateManageUserAsync } from 'src/app/store/createManageUser/async';
import { errorsFetchingClients, fetchingClients, getClientsOfAdmin, getManagingUserInfo } from 'src/app/store/createManageUser/selector';
import { ICreateManageUserState, IManageuser } from 'src/app/store/createManageUser/state';

@Component({
    selector: 'bmt-profile-authorizations',
    templateUrl: './authorizations.component.html',
    styleUrls: ['./authorizations.component.scss'],
    standalone: false
})
export class AuthorizationsComponent implements OnDestroy {
  luxId = 'BMT-profile-auth-comp' + this.constructor.name;
  private readonly destroyRef = inject(DestroyRef);

  readonly defaultPageSize = 50;
  readonly clientsInfo = toSignal(this.lux.get(getClientsOfAdmin, this.luxId), {
    initialValue: {
      clients: [],
      pagination: { limit: 0, offset: 0, totalRecords: 0 },
      sort: []
    } as IClients
  });
  readonly errorsFetchingClients = toSignal(this.lux.get(errorsFetchingClients, this.luxId), { initialValue: '' });
  readonly fetchingClients = toSignal(this.lux.get(fetchingClients, this.luxId), { initialValue: false });
  readonly userInfo = toSignal(this.lux.get(getManagingUserInfo, this.luxId), {
    initialValue: {
      selectedUserId: '',
      selectedAction: '',
      selectedUserType: '',
      adminRole: '',
      contextOfAdmin: '',
      planAcronyms: []
    } as IManageuser
  });
  readonly selectedAdminPlan = toSignal(this.lux.get(getSelectedAdminPlan, this.luxId), { initialValue: '' });
  readonly totalRecords = computed(() => this.clientsInfo()?.pagination?.totalRecords || 0);
  readonly groupData = computed(() => this.clientsInfo()?.clients || []);

  readonly selectedClient = signal('');
  readonly groupSelected = signal<IClient | null>(null);
  private loadedPlansKey = '';

  constructor(
    private readonly lux: Lux<{ createManageUserState: ICreateManageUserState, login: ILoginState }>,
    private readonly createManageUserAsync: CreateManageUserAsync
  ) {
    effect(() => {
      const planAcronyms = this.userInfo()?.planAcronyms || [];
      const planKey = planAcronyms.join('|');

      if (!planAcronyms.length) {
        this.loadedPlansKey = '';
        return;
      }

      if (planKey === this.loadedPlansKey) {
        return;
      }

      this.loadedPlansKey = planKey;
      this.getClients(0);
    });
  }

  getClients(offset: number): void {
    const planAcronyms = this.userInfo()?.planAcronyms || [];
    if (!planAcronyms.length) {
      return;
    }

    const req = {
      planAcronym: planAcronyms,
      offset,
      limit: this.defaultPageSize
    };
    this.createManageUserAsync.getClients(req)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  getMoreClients(page: { pageIndex: number }): void {
    this.getClients(page.pageIndex >= 1 ? (page.pageIndex * this.defaultPageSize) : 0);
  }

  openSubGroupAuths(group: IClient): void {
    this.selectedClient.set(group.specifiedId);
    this.groupSelected.set(group);
  }

  closeSubGroupAuths(): void {
    this.selectedClient.set('');
    this.groupSelected.set(null);
  }

  ngOnDestroy(): void {
    this.lux.destroy(this.luxId);
  }
}
